import { LinkedDocument, ParsedDateExtraction } from '@/core/api/api.copilot.utils';
import type {
  MapCopilotBaseResponse,
  QueryCopilotData,
  ReportsCopilotBaseResponse,
} from '@/pages/api/map-events/query-copilot.types';

export enum MessageAuthor {
  User = 'user',
  Copilot = 'copilot',
}

export type MessageByUser = {
  message: string;
  messageId: string;
  selected_dates?: QueryCopilotData['selected_dates'];
  /** Mode-specific extra context — future modes store their data here */
  metadata?: Record<string, unknown>;
  time: ReturnType<Date['valueOf']>;
  author: MessageAuthor.User;
};

export type StoredLinkedDocument = {
  citation_number: LinkedDocument['citation_number'];
  raw_content: LinkedDocument['raw_content'];
  doc_name: LinkedDocument['metadata']['doc_name'];
  file_path: LinkedDocument['metadata']['file_path'];
};

export type DateExtractionWithQuery = ParsedDateExtraction & {
  question: string;
};

export type MessageByCopilot = {
  message: string;
  citations?: string; // created from linked_documents event, stringify StoredLinkedDocument[]
  dateExtraction?: DateExtractionWithQuery; // date extraction event with confirmation flag
  messageId: string;
  subsetPath?: string;
  isReady?: boolean;
  /** Mode-specific extra data — future modes store their data here without modifying this type */
  metadata?: Record<string, unknown>;
  time: ReturnType<Date['valueOf']>;
  author: MessageAuthor.Copilot;
  responseType:
    | ReportsCopilotBaseResponse['datasource']
    | MapCopilotBaseResponse['flag']
    | 'client_error'
    | 'stream'
    | null;
};

export type Message = MessageByUser | MessageByCopilot;

// ---------------------------------------------------------------------------
// Shared interfaces for mode-agnostic communication architecture
// ---------------------------------------------------------------------------

export interface CopilotQueryFlowResult {
  messageId: string;
  response: string;
  citations: string | undefined;
  dateExtraction: DateExtractionWithQuery | undefined;
  subsetPath: string;
  responseType: MessageByCopilot['responseType'];
  isFetching: boolean;
  error: Error | null;
  runQuery: () => Promise<void>;
}

export interface CopilotCommunicationResult {
  messages: Message[];
  isFetching: boolean;
  addUserMessage: (params: {
    message: string;
    messageId: string;
    selected_dates?: QueryCopilotData['selected_dates'];
    metadata?: Record<string, unknown>;
  }) => void;
}
